// Where one candidate stands in the WhatsApp pre-screen, and the note about
// them. Opened from the status line under their phone number.
//
// The reply cannot be read automatically — WhatsApp Web gives this page no way
// to see it — so the recruiter records it: Interested and the time they picked,
// or not. Every save is also written to the candidate's history on the server.

import React, { useEffect, useState } from "react";
import { Box, Button, Popover, Stack, TextField, Typography } from "@mui/material";

const FONT = "'Plus Jakarta Sans', sans-serif";
const INK = "#18181b";
const MUTED = "#71717a";
const FAINT = "#a1a1aa";
const LINE = "#e4e4e7";

// The order a conversation goes in. `null` is "not contacted".
export const WA_STATUS = {
  null: { label: "Not contacted", short: "To contact", dot: "#d4d4d8" },
  MESSAGED: { label: "Messaged", short: "Messaged", dot: "#0891b2" },
  INTERESTED: { label: "Interested", short: "Interested", dot: "#059669" },
  NO_REPLY: { label: "No reply", short: "No reply", dot: "#d97706" },
  NOT_INTERESTED: { label: "Not interested", short: "Not interested", dot: "#b91c1c" },
  // The override: cleared to invite without WhatsApp, e.g. agreed on a call.
  SKIPPED: { label: "WhatsApp skipped", short: "Skipped", dot: "#6366f1" },
};

const CHOICES = [null, "MESSAGED", "INTERESTED", "NO_REPLY", "NOT_INTERESTED"];

export default function WhatsAppStatusPopover({ anchorEl, row, onClose, onSave }) {
  const open = Boolean(anchorEl && row);
  const wa = row?.whatsapp || {};
  const [status, setStatus] = useState(wa.status ?? null);
  const [slot, setSlot] = useState(wa.call_slot || "");
  const [note, setNote] = useState(wa.note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStatus(row?.whatsapp?.status ?? null);
    setSlot(row?.whatsapp?.call_slot || "");
    setNote(row?.whatsapp?.note || "");
    setError(null);
  }, [open, row]);

  // Only what changed goes to the server, so the history records what was
  // actually done rather than re-recording every field on every save.
  const save = async (override) => {
    const next = override ?? { status, call_slot: slot.trim() || null, note: note.trim() || null };
    const patch = {};
    if (next.status !== (wa.status ?? null)) patch.status = next.status;
    if ("call_slot" in next && next.call_slot !== (wa.call_slot || null)) patch.call_slot = next.call_slot;
    if ("note" in next && next.note !== (wa.note || null)) patch.note = next.note;
    if (!Object.keys(patch).length) return onClose();
    setSaving(true);
    setError(null);
    try {
      await onSave(row, patch);
      onClose();
    } catch (err) {
      setError(err.response?.data?.statusMessage || err.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={saving ? undefined : onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { borderRadius: 2.5, p: 2, width: 320, border: `1px solid ${LINE}` } }}
    >
      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: INK }} noWrap>
        {row?.candidate_name} on WhatsApp
      </Typography>

      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
        {CHOICES.map((key) => {
          const s = WA_STATUS[key];
          const on = status === key;
          return (
            <Box
              key={String(key)}
              onClick={() => !saving && setStatus(key)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.1,
                py: 0.5,
                borderRadius: 2,
                cursor: saving ? "default" : "pointer",
                fontFamily: FONT,
                fontSize: 12,
                fontWeight: on ? 700 : 550,
                color: on ? "#fff" : MUTED,
                bgcolor: on ? INK : "#fff",
                border: `1px solid ${on ? INK : LINE}`,
                userSelect: "none",
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: s.dot }} />
              {s.label}
            </Box>
          );
        })}
      </Stack>

      <TextField
        size="small"
        fullWidth
        label="Call time they picked"
        placeholder="e.g. 6 pm"
        value={slot}
        onChange={(e) => setSlot(e.target.value)}
        disabled={saving}
        inputProps={{ maxLength: 60, style: { fontFamily: FONT, fontSize: 13 } }}
        InputLabelProps={{ sx: { fontFamily: FONT, fontSize: 13 } }}
        sx={{ mt: 1.75 }}
      />

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        label="Note"
        placeholder="e.g. Asked to call after 8 pm · expecting 4 LPA"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={saving}
        inputProps={{ maxLength: 1000, style: { fontFamily: FONT, fontSize: 13 } }}
        InputLabelProps={{ sx: { fontFamily: FONT, fontSize: 13 } }}
        sx={{ mt: 1.5 }}
      />

      {error && (
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#b91c1c", mt: 1 }}>{error}</Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.75 }}>
        {/* The override. Separate from the status chips and worded as what it
            does, because it is the one choice here that lets an invite through
            without the candidate having agreed on WhatsApp. */}
        {wa.status !== "SKIPPED" ? (
          <Button
            size="small"
            disabled={saving}
            onClick={() => save({ status: "SKIPPED" })}
            title="For a candidate who agreed some other way, e.g. on a phone call. Recorded in their history."
            sx={{ fontFamily: FONT, textTransform: "none", fontSize: 12, color: FAINT, px: 0.5, minWidth: 0 }}
          >
            Skip WhatsApp
          </Button>
        ) : (
          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: FAINT }}>Skipped — may be invited</Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          onClick={onClose}
          disabled={saving}
          sx={{ fontFamily: FONT, textTransform: "none", fontWeight: 650, color: MUTED }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={saving}
          onClick={() => save()}
          sx={{ fontFamily: FONT, textTransform: "none", fontWeight: 700, bgcolor: INK, "&:hover": { bgcolor: "#27272a" } }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Stack>
    </Popover>
  );
}
